import { useForm, FormProvider, useFieldArray } from "react-hook-form";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useEffect } from "react";
import { EditBookingCharterInfoForm, updateBookingCharterInfoSchema } from "./bookingCharterInfo.schema";
import { useBookingNotesMutation } from "@/src/lib/bookingNotesMutation";
import { useToast } from "@/src/components/ui/use-toast";
import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { DatePicker } from "@/src/components/ui/date-picker";
import { format, parse, parseISO, set } from "date-fns";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { TimeSelect } from "@/src/components/ui/time-select";
import { Combobox } from "@/src/components/ui/combobox";
import { fetch } from "@/src/lib/fetch";
import { Location } from "@/src/app/Locations/model";
import { z } from "zod";
import { useBookingCharterDataMutation } from "@/src/lib/bookingCharterDataMutation";
import { extractFromMultiName } from "../../Locations/utils";

type EditBookingCharterInfoModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialData: z.infer<typeof updateBookingCharterInfoSchema>;
  bookingId: string;
  offerYachtId: string;
};

export const EditBookingCharterInfoModal = ({
  open,
  setOpen,
  initialData,
  bookingId,
  offerYachtId,
}: EditBookingCharterInfoModalProps) => {
  console.log("initialData", initialData);
  const updateBookingCharterData = useBookingCharterDataMutation(bookingId, offerYachtId);
  const { toast } = useToast();

  const form = useForm<EditBookingCharterInfoForm>({
    defaultValues: {
      startDate: initialData.startDate,
      endDate: initialData.endDate,
      startLocation: initialData.startLocation,
      endLocation: initialData.endLocation,
    },
  });

  const { handleSubmit, control, reset } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "notes" as never,
  });

  useEffect(() => {
    reset({
      startDate: initialData.startDate || undefined,
      endDate: initialData.endDate || undefined,
      startTime: initialData.startTime || undefined,
      endTime: initialData.endTime || undefined,
      startLocation: initialData.startLocation || undefined,
      endLocation: initialData.endLocation || undefined,
    });
  }, []);

  const onSubmit = (data: EditBookingCharterInfoForm) => {
    console.log("Raw Input:", {
      startDate: data.startDate,
      startTime: data.startTime,
      endDate: data.endDate,
      endTime: data.endTime,
    });

    const replaceTime = (dateStr: string, timeStr: string) => {
      const parsedDate = formatDateUTC(dateStr); // Ensure the date is parsed correctly
      const [hours, minutes] = timeStr.split(":").map(Number);

      const localDate = set(parsedDate, { hours, minutes, seconds: 0, milliseconds: 0 });

      return new Date(
        Date.UTC(
          localDate.getFullYear(),
          localDate.getMonth(),
          localDate.getDate(),
          localDate.getHours(),
          localDate.getMinutes(),
        ),
      );
    };

    // Process start and end dates
    const charterStartDate = replaceTime(data.startDate, data.startTime);
    const charterEndDate = replaceTime(data.endDate, data.endTime);

    console.log("Parsed Dates:", { charterStartDate, charterEndDate });
    const preparedData = {
      charterStartDate,
      charterEndDate,
      startLocationId: data.startLocation.id,
      endLocationId: data.endLocation.id,
    };

    console.log("Prepared Data:", preparedData);

    const toastId = toast({
      description: "Updating charter data...",
      variant: "default",
      duration: Infinity,
    }).id;

    updateBookingCharterData.mutate(
      { charterData: preparedData, toastId },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  };

  const normalizeDate = (dateString: string): Date => {
    const parsedDate = new Date(dateString); // Parse date in UTC

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    // Return a new Date object that represents the correct UTC date
    return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        if (!val) {
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-[640px] max-h-[calc(100%-60px)] overflow-y-auto">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle className="text-3xl font-semi-bold">Edit Charter Data</DialogTitle>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-4">
                  <FormField<EditBookingCharterInfoForm, "startDate">
                    name="startDate"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>Start date</FormLabel>
                          <FormControl>
                            <DatePicker
                              disabledDays={{ before: new Date() }}
                              className="w-full"
                              onChange={(v) => {
                                if (v) {
                                  const formattedDate = format(v, "yyyy-MM-dd");
                                  field.onChange(v ? formattedDate : null);
                                  return v;
                                } else {
                                  field.onChange(null);
                                  return formatDateUTC(v);
                                }
                              }}
                              value={field.value ? normalizeDate(field.value) : undefined}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                  <FormField<EditBookingCharterInfoForm, "startTime">
                    name="startTime"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>Start time</FormLabel>
                          <FormControl>
                            <TimeSelect onChange={field.onChange} value={field.value} />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />

                  <FormField<EditBookingCharterInfoForm, "startLocation">
                    name="startLocation"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>Start location</FormLabel>
                          <FormControl>
                            <Combobox<Location>
                              className="w-full"
                              placeholder="Search for location"
                              loadOptions={(query) =>
                                fetch<{ data: Location[] }>(
                                  `/api/locations/locations?pageSize=5${query ? `&filter=${query}` : ""}`,
                                ).then((r) => r.data)
                              }
                              value={(field.value?.id ? field.value : null) as any}
                              onChange={(v) => {
                                field.onChange(v && { id: v.id, names: v.names });
                              }}
                              renderOption={(r) => extractFromMultiName(r.names)}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <FormField<EditBookingCharterInfoForm, "endDate">
                    name="endDate"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>End date</FormLabel>
                          <FormControl>
                            <DatePicker
                              disabledDays={{ before: new Date() }}
                              className="w-full"
                              onChange={(v) => {
                                if (v) {
                                  const formattedDate = format(v, "yyyy-MM-dd");
                                  field.onChange(v ? formattedDate : null);
                                  return formatDateUTC(v);
                                } else {
                                  field.onChange(null);
                                  return v;
                                }
                              }}
                              value={field.value ? normalizeDate(field.value) : undefined}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                  <FormField<EditBookingCharterInfoForm, "endTime">
                    name="endTime"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>End time</FormLabel>
                          <FormControl>
                            <TimeSelect onChange={field.onChange} value={field.value} />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />

                  <FormField<EditBookingCharterInfoForm, "endLocation">
                    name="endLocation"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>End location</FormLabel>
                          <FormControl>
                            <Combobox<Location>
                              className="w-full"
                              placeholder="Search for location"
                              loadOptions={(query) =>
                                fetch<{ data: Location[] }>(
                                  `/api/locations/locations?pageSize=5${query ? `&filter=${query}` : ""}`,
                                ).then((r) => r.data)
                              }
                              value={(field.value?.id ? field.value : null) as any}
                              onChange={(v) => {
                                field.onChange(v && { id: v.id, names: v.names });
                              }}
                              renderOption={(r) => extractFromMultiName(r.names)}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="flex mt-8 w-full !justify-between">
              <Button type="button" onClick={() => setOpen(false)} variant="outline">
                Cancel
              </Button>
              <Button type="submit" variant="default">
                Save
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
